import { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { isUserCoach } from '../../lib/auth';
import { fetchReportById, updateReport, type TransformationReport } from '../../lib/transformation-reports';
import { MarkdownRenderer } from '../../components/ai/MarkdownRenderer';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { characteristics } from '../../data/characteristics';
import {
  ArrowLeft, Target, Activity, ClipboardCheck, Trophy, BookOpen,
  Calendar, BarChart2, CheckCircle, Eye,
} from '../../components/icons';

const REPORT_TYPE_LABELS: Record<string, string> = {
  weekly: 'Weekly',
  monthly: 'Monthly',
  programme_end: 'Programme End',
};

function ScoreDelta({ current, previous }: { current: number; previous?: number }) {
  if (previous === undefined) {
    return <span className="text-sm font-bold text-chocolate-800">{Math.round(current)}</span>;
  }
  const delta = Math.round(current - previous);
  return (
    <span className="flex items-center gap-1">
      <span className="text-sm font-bold text-chocolate-800">{Math.round(current)}</span>
      {delta !== 0 && (
        <span className={`text-[11px] font-semibold ${delta > 0 ? 'text-sage-600' : 'text-red-500'}`}>
          {delta > 0 ? `+${delta}` : delta}
        </span>
      )}
    </span>
  );
}

function MiniBar({ value, max = 100, color }: { value: number; max?: number; color: string }) {
  return (
    <div className="flex-1 h-2 bg-cream-200 rounded-full overflow-hidden">
      <div className={`h-full rounded-full ${color}`} style={{ width: `${(value / max) * 100}%` }} />
    </div>
  );
}

export function TransformationReportDetail() {
  const { userId, reportId } = useParams<{ userId: string; reportId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [isLoading, setIsLoading] = useState(true);
  const [report, setReport] = useState<TransformationReport | null>(null);
  const [respondentName, setRespondentName] = useState('');
  const [coachNotes, setCoachNotes] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [generatingNarrative, setGeneratingNarrative] = useState(false);

  useEffect(() => {
    if (!user || !reportId || !userId) return;
    const init = async () => {
      const isCoach = await isUserCoach(user.id);
      if (!isCoach) { navigate('/coach'); return; }

      const [r, { data: userData }] = await Promise.all([
        fetchReportById(reportId),
        supabase.from('users').select('full_name, email').eq('id', userId).maybeSingle(),
      ]);

      if (!r) { navigate(`/coach/proof/${userId}/reports`); return; }
      setReport(r);
      setCoachNotes(r.coach_notes || '');
      setRespondentName(userData?.full_name || userData?.email || userId);
      setIsLoading(false);
    };
    init();
  }, [user, reportId, userId, navigate]);

  const handleSaveNotes = async () => {
    if (!report) return;
    setSavingNotes(true);
    await updateReport(report.id, { coach_notes: coachNotes });
    setReport(r => r ? { ...r, coach_notes: coachNotes } : r);
    setSavingNotes(false);
  };

  const handleTogglePublish = async () => {
    if (!report) return;
    setPublishing(true);
    const newVal = !report.is_published;
    await updateReport(report.id, { is_published: newVal });
    setReport(r => r ? { ...r, is_published: newVal } : r);
    setPublishing(false);
  };

  const handleGenerateNarrative = async () => {
    if (!report) return;
    setGeneratingNarrative(true);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData.session?.access_token;
      const res = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/generate-transformation-report`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({
            quantitative: report.quantitative,
            clientName: respondentName,
            reportType: report.report_type,
          }),
        },
      );
      if (res.ok) {
        const { narrative } = await res.json();
        if (narrative) {
          await updateReport(report.id, { ai_narrative: narrative });
          setReport(r => r ? { ...r, ai_narrative: narrative } : r);
        }
      }
    } finally {
      setGeneratingNarrative(false);
    }
  };

  if (isLoading) return <LoadingSpinner message="Loading report..." />;
  if (!report) return null;

  const q = report.quantitative;

  // Build score comparison map
  const latestScoreMap = new Map<string, number>();
  const previousScoreMap = new Map<string, number>();
  (q.latestAssessment?.scores || []).forEach((s: any) => latestScoreMap.set(s.characteristicId, s.normalized));
  (q.previousAssessment?.scores || []).forEach((s: any) => previousScoreMap.set(s.characteristicId, s.normalized));

  return (
    <div className="p-6 md:p-8 max-w-4xl">
      {/* Header */}
      <div className="mb-6">
        <Link
          to={`/coach/proof/${userId}/reports`}
          className="flex items-center gap-1.5 text-sm text-chocolate-500 hover:text-chocolate-700 mb-4 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          All reports for {respondentName}
        </Link>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">
                {REPORT_TYPE_LABELS[report.report_type] || report.report_type}
              </span>
              {report.is_published && (
                <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-green-100 text-green-700">Published to client</span>
              )}
            </div>
            <h1 className="freud-heading-xl text-chocolate-900">{report.title}</h1>
            <p className="text-sm text-chocolate-500 mt-1 flex items-center gap-1.5">
              <Calendar className="w-3.5 h-3.5" />
              {new Date(report.period_start).toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' })} –{' '}
              {new Date(report.period_end).toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' })}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleTogglePublish}
              disabled={publishing}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all border ${
                report.is_published
                  ? 'border-green-300 bg-green-50 text-green-700 hover:bg-green-100'
                  : 'border-chocolate-200 text-chocolate-600 hover:border-chocolate-400'
              }`}
            >
              <Eye className="w-4 h-4" />
              {report.is_published ? 'Unpublish' : 'Publish to Client'}
            </button>
          </div>
        </div>
      </div>

      <div className="space-y-6">
        {/* Programme-end: Full Journey Summary banner */}
        {report.report_type === 'programme_end' && (() => {
          const start = new Date(report.period_start);
          const end = new Date(report.period_end);
          const weeks = Math.round((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24 * 7));
          return (
            <div className="freud-card-elevated p-6 bg-gradient-to-r from-amber-50 to-cream-50 border border-amber-200">
              <div className="flex items-center gap-2 mb-3">
                <Trophy className="w-5 h-5 text-amber-600" />
                <h2 className="freud-heading-lg text-chocolate-900">Full Programme Summary</h2>
              </div>
              <p className="text-sm text-chocolate-600 mb-4">
                This report covers the complete coaching programme from{' '}
                <strong>{start.toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' })}</strong> to{' '}
                <strong>{end.toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' })}</strong>
                {weeks > 0 && ` (${weeks} weeks)`}.
              </p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {[
                  { label: 'Goals Completed', value: q.goalsCompleted, color: 'text-sage-600 bg-sage-100' },
                  { label: 'Milestones Achieved', value: q.milestonesInPeriod, color: 'text-yellow-700 bg-yellow-100' },
                  { label: 'Pathways Completed', value: q.pathwayCompletionCount, color: 'text-blue-600 bg-blue-100' },
                  { label: 'Check-ins Logged', value: q.checkInStats?.count || 0, color: 'text-coral-600 bg-coral-100' },
                ].map(({ label, value, color }) => (
                  <div key={label} className="bg-white/70 rounded-xl p-3 border border-cream-200 text-center">
                    <p className={`text-2xl font-bold ${color.split(' ')[0]}`}>{value}</p>
                    <p className="text-[11px] text-chocolate-500 mt-0.5">{label}</p>
                  </div>
                ))}
              </div>
            </div>
          );
        })()}

        {/* Quantitative Summary */}
        <div className="freud-card-elevated p-6">
          <h2 className="freud-heading-lg text-chocolate-900 mb-4 flex items-center gap-2">
            <BarChart2 className="w-5 h-5 text-chocolate-400" />
            Quantitative Summary
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            {[
              { label: 'Active Goals', value: q.goalsActive, sub: `${q.goalsCompleted} completed${report.report_type === 'programme_end' ? ' total' : ' in period'}`, icon: Target, color: 'bg-sage-100 text-sage-600' },
              { label: 'Habit Rate', value: `${q.overallHabitRate}%`, sub: `${q.habitStats?.length || 0} habits tracked`, icon: Activity, color: 'bg-coral-100 text-coral-600' },
              { label: 'Tasks Done', value: q.tasksCompleted, sub: `${q.tasksOpen} still open`, icon: ClipboardCheck, color: 'bg-amber-100 text-amber-600' },
              { label: report.report_type === 'programme_end' ? 'Total Milestones' : 'Milestones', value: q.milestonesInPeriod, sub: report.report_type === 'programme_end' ? 'across the programme' : 'in this period', icon: Trophy, color: 'bg-yellow-100 text-yellow-600' },
            ].map(({ label, value, sub, icon: Icon, color }) => (
              <div key={label} className="bg-cream-50 rounded-xl p-4 border border-cream-200">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center mb-2 ${color}`}>
                  <Icon className="w-4 h-4" />
                </div>
                <p className="text-2xl font-bold text-chocolate-900">{value}</p>
                <p className="text-xs text-chocolate-500">{label}</p>
                <p className="text-[10px] text-chocolate-400 mt-0.5">{sub}</p>
              </div>
            ))}
          </div>

          {/* Check-ins */}
          {q.checkInStats && q.checkInStats.count > 0 && (
            <div className="border-t border-cream-200 pt-4 mb-4">
              <h3 className="text-sm font-semibold text-chocolate-700 mb-3">Wellbeing Check-ins ({q.checkInStats.count} {report.report_type === 'programme_end' ? 'total' : 'in period'})</h3>
              <div className="space-y-2">
                {[
                  { label: 'Energy', value: q.checkInStats.avgEnergy, color: 'bg-amber-400' },
                  { label: 'Mood', value: q.checkInStats.avgMood, color: 'bg-coral-400' },
                  { label: 'Focus', value: q.checkInStats.avgFocus, color: 'bg-blue-400' },
                ].map(({ label, value, color }) => (
                  <div key={label} className="flex items-center gap-3">
                    <span className="text-xs text-chocolate-500 w-14">{label}</span>
                    <MiniBar value={value} max={10} color={color} />
                    <span className="text-sm font-bold text-chocolate-800 w-8 text-right">{value}/10</span>
                  </div>
                ))}
              </div>
              {q.checkInStats.trend && q.checkInStats.trend.length > 1 && (
                <div className="mt-3 flex items-end gap-0.5 h-10">
                  {q.checkInStats.trend.map((pt: any, i: number) => (
                    <div
                      key={i}
                      title={`${new Date(pt.date).toLocaleDateString()}: ${pt.avg}`}
                      style={{ height: `${(pt.avg / 10) * 100}%` }}
                      className="flex-1 bg-sage-400 rounded-sm opacity-70 hover:opacity-100 transition-opacity min-h-[2px]"
                    />
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Habits */}
          {q.habitStats && q.habitStats.length > 0 && (
            <div className="border-t border-cream-200 pt-4 mb-4">
              <h3 className="text-sm font-semibold text-chocolate-700 mb-3">Habit Performance</h3>
              <div className="space-y-2">
                {q.habitStats.map((h: any) => (
                  <div key={h.title} className="flex items-center gap-3">
                    <span className="text-xs text-chocolate-600 truncate flex-1 min-w-0">{h.title}</span>
                    <div className="flex items-center gap-2 w-36 flex-shrink-0">
                      <MiniBar value={h.completionRate} color="bg-sage-500" />
                      <span className={`text-xs font-semibold w-9 text-right ${h.completionRate >= 70 ? 'text-sage-600' : h.completionRate >= 40 ? 'text-amber-600' : 'text-red-500'}`}>
                        {h.completionRate}%
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Active Goals */}
          {q.topActiveGoals && q.topActiveGoals.length > 0 && (
            <div className="border-t border-cream-200 pt-4">
              <h3 className="text-sm font-semibold text-chocolate-700 mb-2">
                {report.report_type === 'programme_end' ? 'Goals at Programme End' : 'Active Goals'}
              </h3>
              <ul className="space-y-1.5">
                {q.topActiveGoals.map((g: any, i: number) => (
                  <li key={i} className="flex items-center gap-2 text-sm text-chocolate-700">
                    <CheckCircle className="w-3.5 h-3.5 text-chocolate-200 flex-shrink-0" />
                    <span className="flex-1">{g.title}</span>
                    {g.targetDate && <span className="text-[10px] text-chocolate-400">by {new Date(g.targetDate).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}</span>}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Programme-end: completed goals count callout */}
          {report.report_type === 'programme_end' && q.goalsCompleted > 0 && (
            <div className="border-t border-cream-200 pt-4">
              <div className="flex items-center gap-2 text-sage-700 bg-sage-50 rounded-xl px-4 py-3 border border-sage-100">
                <CheckCircle className="w-4 h-4 text-sage-600 flex-shrink-0" />
                <span className="text-sm font-medium">{q.goalsCompleted} goal{q.goalsCompleted !== 1 ? 's' : ''} completed across this programme</span>
              </div>
            </div>
          )}
        </div>

        {/* Assessment Profile — always shown if any assessment exists */}
        {q.latestAssessment && (
          <div className="freud-card-elevated p-6">
            <h2 className="freud-heading-lg text-chocolate-900 mb-1 flex items-center gap-2">
              <BarChart2 className="w-5 h-5 text-chocolate-400" />
              {q.previousAssessment ? 'Assessment Comparison' : 'Baseline Profile Scores'}
            </h2>
            {q.previousAssessment ? (
              <p className="text-sm text-chocolate-500 mb-4">
                v{q.previousAssessment.version} ({new Date(q.previousAssessment.completedAt).toLocaleDateString()}) → v{q.latestAssessment.version} ({new Date(q.latestAssessment.completedAt).toLocaleDateString()}) — green/red deltas show movement
              </p>
            ) : (
              <p className="text-sm text-chocolate-500 mb-4">
                Starting baseline — assessment v{q.latestAssessment.version} completed {new Date(q.latestAssessment.completedAt).toLocaleDateString()}. A second assessment will reveal movement over time.
              </p>
            )}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {characteristics.slice(0, 12).map(c => {
                const score = latestScoreMap.get(c.id);
                if (score === undefined) return null;
                const prev = previousScoreMap.get(c.id);
                return (
                  <div key={c.id} className="flex items-center gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-0.5">
                        <span className="text-xs text-chocolate-600 truncate">{c.name}</span>
                        <ScoreDelta current={score} previous={prev} />
                      </div>
                      <MiniBar value={score} color="bg-sage-500" />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Milestones */}
        {q.milestonesList && q.milestonesList.length > 0 && (
          <div className="freud-card-elevated p-6">
            <h2 className="freud-heading-lg text-chocolate-900 mb-4 flex items-center gap-2">
              <Trophy className="w-5 h-5 text-yellow-500" />
              Milestones Achieved
            </h2>
            <div className="space-y-2">
              {q.milestonesList.map((m: any, i: number) => (
                <div key={i} className="flex items-center gap-3 py-2 border-b border-cream-100 last:border-0">
                  <Trophy className="w-4 h-4 text-yellow-500 flex-shrink-0" />
                  <span className="flex-1 text-sm text-chocolate-800">{m.title}</span>
                  <span className="text-xs text-chocolate-400">{new Date(m.achievedAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Journal Excerpts */}
        {q.journalExcerpts && q.journalExcerpts.length > 0 && (
          <div className="freud-card-elevated p-6">
            <h2 className="freud-heading-lg text-chocolate-900 mb-4 flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-chocolate-400" />
              Journal Reflections ({q.journalEntriesInPeriod} entries)
            </h2>
            <div className="space-y-4">
              {q.journalExcerpts.map((j: any, i: number) => (
                <div key={i} className="border-l-2 border-sage-200 pl-4">
                  <p className="text-xs text-chocolate-400 mb-1">{new Date(j.date).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })} — {j.title}</p>
                  <p className="text-sm text-chocolate-700 italic">"{j.content}{j.content.length >= 200 ? '…' : ''}"</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Coaching sessions */}
        {q.sessionNotes && q.sessionNotes.length > 0 && (
          <div className="freud-card-elevated p-6">
            <h2 className="freud-heading-lg text-chocolate-900 mb-4 flex items-center gap-2">
              <Calendar className="w-5 h-5 text-chocolate-400" />
              Coaching Sessions ({q.sessionCount})
            </h2>
            <div className="space-y-3">
              {q.sessionNotes.map((s: any, i: number) => (
                <div key={i} className="text-sm">
                  <p className="text-xs font-semibold text-chocolate-500 mb-1">{new Date(s.date).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}</p>
                  {s.notes && <p className="text-chocolate-700 mb-1">{s.notes}</p>}
                  {s.followUps && <p className="text-chocolate-500 text-xs">Follow-ups: {s.followUps}</p>}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* AI Narrative */}
        <div className="freud-card-elevated p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="freud-heading-lg text-chocolate-900">AI Transformation Narrative</h2>
            {!report.ai_narrative && (
              <button
                onClick={handleGenerateNarrative}
                disabled={generatingNarrative}
                className="freud-btn-primary flex items-center gap-2 text-sm"
              >
                {generatingNarrative ? (
                  <><div className="w-3.5 h-3.5 border-2 border-white/40 border-t-white rounded-full animate-spin" />Generating...</>
                ) : 'Generate AI Narrative'}
              </button>
            )}
          </div>
          {report.ai_narrative ? (
            <div className="prose-sm max-w-none">
              <MarkdownRenderer content={report.ai_narrative} />
            </div>
          ) : (
            <p className="text-sm text-chocolate-400 italic">No AI narrative yet. Click "Generate AI Narrative" to create one.</p>
          )}
          {report.ai_narrative && (
            <button
              onClick={handleGenerateNarrative}
              disabled={generatingNarrative}
              className="mt-4 text-xs text-chocolate-400 hover:text-chocolate-600 transition-colors"
            >
              {generatingNarrative ? 'Regenerating...' : 'Regenerate'}
            </button>
          )}
        </div>

        {/* Coach Notes */}
        <div className="freud-card-elevated p-6">
          <h2 className="freud-heading-lg text-chocolate-900 mb-4">Coach Notes</h2>
          <textarea
            className="freud-input w-full"
            rows={5}
            placeholder="Add your observations, breakthroughs, and recommendations here. These notes are included in the client report when published."
            value={coachNotes}
            onChange={e => setCoachNotes(e.target.value)}
          />
          <div className="flex justify-end mt-3">
            <button
              onClick={handleSaveNotes}
              disabled={savingNotes}
              className="freud-btn-primary"
            >
              {savingNotes ? 'Saving...' : 'Save Notes'}
            </button>
          </div>
        </div>

        {/* Publish action */}
        <div className="freud-card-elevated p-5 bg-sage-50 border border-sage-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-chocolate-900">
                {report.is_published ? 'This report is visible to the client.' : 'This report is not yet visible to the client.'}
              </p>
              <p className="text-sm text-chocolate-500 mt-0.5">
                {report.is_published ? 'The client can view this report in their Proof > My Reports section.' : 'Click Publish to share it with the client.'}
              </p>
            </div>
            <button
              onClick={handleTogglePublish}
              disabled={publishing}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                report.is_published
                  ? 'bg-cream-200 text-chocolate-700 hover:bg-cream-300'
                  : 'bg-sage-500 text-white hover:bg-sage-600'
              }`}
            >
              {report.is_published ? 'Unpublish' : 'Publish to Client'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
